import { useEffect, useMemo, useState } from "react";
import { ImageOff } from "lucide-react";
import type { ImmotionAttachment } from "@myapp/contract";
import { DocumentViewer } from "../../overlay/DocumentViewer/DocumentViewer";
import { Lightbox, type LightboxItem } from "../../overlay/Lightbox/Lightbox";
import { formatBytes } from "../../../lib/formatBytes";
import { isStandalone } from "../../../lib/pwa";
import { buildViewAttachments, type ViewAttachment } from "../../../lib/viewAttachments";

type Props = {
  appAttachments: string[] | null | undefined;
  immotionAttachments?: ImmotionAttachment[] | null | undefined;
};

/**
 * One "Anhänge" card, shared by both worker pages. Moved here from
 * `pages/worker-appointment/ui/AppointmentAttachments.tsx` so `pages/worker-task` can use it too —
 * FSD forbids a page importing from another page. `immotionAttachments` is optional: the task page
 * has only app attachments (bare `string[]` paths), the appointment page has both.
 *
 * Tapping a photo opens the in-app `Lightbox`; the worker looks, never saves — there is no
 * download affordance anywhere in this section.
 */
export const AttachmentsCard = ({ appAttachments, immotionAttachments }: Props) => {
  const items = useMemo(
    () => buildViewAttachments(appAttachments, immotionAttachments),
    [appAttachments, immotionAttachments],
  );

  // Immotion attachments are materialised as blob: URLs — revoke them on unmount/change, same as
  // the gallery this replaces did.
  useEffect(
    () => () => {
      items.forEach((item) => {
        if (item.source === "immotion" && item.url) URL.revokeObjectURL(item.url);
      });
    },
    [items],
  );

  const [broken, setBroken] = useState<Record<string, boolean>>({});
  useEffect(() => setBroken({}), [items]);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [viewerItem, setViewerItem] = useState<ViewAttachment | null>(null);

  const images = items.filter((item) => item.descriptor.kind === "image");
  const others = items.filter((item) => item.descriptor.kind !== "image");
  const totalSize = items.reduce((sum, item) => sum + (item.sizeBytes ?? 0), 0);

  const lightboxItems: LightboxItem[] = images.map((item) => ({
    key: item.key,
    url: item.url ?? "",
    label: item.label,
    meta: item.sizeBytes !== null ? formatBytes(item.sizeBytes) : undefined,
  }));

  // A plain `target="_blank"` link is a dead end inside an installed PWA: a standalone window has
  // no browser chrome, and a same-origin navigation replaces the app in place with nothing on
  // screen to get back with. Only there does a document row open the in-app viewer instead —
  // see `DocumentViewer`'s own comment for the full story.
  const openInApp = isStandalone();

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Anhänge ({items.length})
        </p>
        {totalSize > 0 && (
          <span className="text-xs text-slate-400">{formatBytes(totalSize)}</span>
        )}
      </div>

      {items.length === 0 ? (
        <p className="mt-2 text-sm text-slate-400">Keine Anhänge</p>
      ) : (
        <>
          {images.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {images.map((item, imageIndex) => {
                const failed = !item.url || broken[item.key];

                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setLightboxIndex(imageIndex)}
                    disabled={failed}
                    // `min-w-0`: grid items default to `min-width: auto`, and with only one photo
                    // there is no sibling column forcing the track down — the `aspect-square` box
                    // then falls back to its intrinsic size instead of the 1/3-width track, and the
                    // thumbnail renders full-size instead of cropped. The classic CSS "grid
                    // blowout" (https://css-tricks.com/preventing-a-grid-blowout/).
                    className="flex min-w-0 flex-col gap-1 text-left disabled:cursor-default"
                  >
                    <span
                      className="block aspect-square w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-100"
                      style={{ aspectRatio: "1 / 1" }}
                    >
                      {failed ? (
                        <span className="flex h-full w-full items-center justify-center text-slate-400">
                          <ImageOff size={20} aria-hidden="true" />
                        </span>
                      ) : (
                        <img
                          src={item.url ?? undefined}
                          alt={item.label}
                          loading="lazy"
                          onError={() => setBroken((prev) => ({ ...prev, [item.key]: true }))}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </span>
                    <span className="truncate text-xs text-slate-500">{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {others.length > 0 && (
            <ul className="mt-3 flex flex-col gap-2">
              {others.map((item) => {
                const Icon = item.descriptor.Icon;
                const rowContent = (
                  <>
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white ${item.descriptor.tint}`}>
                      <Icon size={18} aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-900">
                        {item.label}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {item.descriptor.ext.toUpperCase()}
                        {item.sizeBytes !== null && ` · ${formatBytes(item.sizeBytes)}`}
                      </span>
                    </span>
                  </>
                );
                const rowClassName =
                  "flex min-h-[44px] w-full items-center gap-3 rounded-xl bg-[#EEF1F7] px-3 py-2 text-left";

                return (
                  <li key={item.key}>
                    {openInApp ? (
                      <button
                        type="button"
                        onClick={() => setViewerItem(item)}
                        disabled={!item.url}
                        className={`${rowClassName} disabled:pointer-events-none disabled:opacity-50`}
                      >
                        {rowContent}
                      </button>
                    ) : (
                      <a
                        href={item.url ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        aria-disabled={item.url ? undefined : true}
                        className={`${rowClassName} aria-disabled:pointer-events-none aria-disabled:opacity-50`}
                      >
                        {rowContent}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <Lightbox
        items={lightboxItems}
        index={lightboxIndex ?? 0}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        open={lightboxIndex !== null}
      />

      <DocumentViewer
        url={viewerItem?.url ?? null}
        label={viewerItem?.label ?? ""}
        onClose={() => setViewerItem(null)}
        open={viewerItem !== null}
      />
    </div>
  );
};
